import { useState } from "react";
import { toast } from "sonner";
import { Loader2, ReceiptText, Check, X, FileText } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useVendorPaymentsReview, useVendorRefresh } from "@/hooks/useVendor";
import { formatPrice, formatDate, formatDateTime } from "@/lib/format";
import { Pill } from "@/pages/admin/ui";
import WhatsAppLink from "@/components/WhatsAppLink";

export default function VendorPaymentsVerify() {
  const [tab, setTab] = useState("review");
  const { data, isLoading } = useVendorPaymentsReview(tab);
  const refresh = useVendorRefresh();
  const [busy, setBusy] = useState("");

  const orders = data || [];

  const verify = async (o, action) => {
    const note = action === "reject" ? window.prompt("Motif du refus (visible par le client) :") || "" : "";
    if (action === "reject" && note === null) return;
    setBusy(o.id + action);
    try {
      await api.post(`/vendor/payments/${o.id}/verify`, { action, note });
      toast.success(action === "confirm" ? "Paiement confirmé — commande en traitement" : "Paiement refusé");
      refresh(["vendor-payments", "vendor-badges", "vendor-orders", "vendor-dashboard"]);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  const viewProof = async (o) => {
    try {
      const { data: blob } = await api.get(`/vendor/payments/${o.id}/proof`, { responseType: "blob" });
      window.open(URL.createObjectURL(blob), "_blank");
    } catch { toast.error("Aucune preuve disponible"); }
  };

  return (
    <main className="space-y-5" data-testid="vendor-payments-verify">
      <div>
        <h1 className="font-display text-2xl font-extrabold text-ink">Paiements à vérifier</h1>
        <p className="mt-1 text-sm text-slate-500">Vérifiez les paiements déclarés par vos clients.</p>
      </div>

      <div className="flex gap-2">
        <button onClick={() => setTab("review")} data-testid="tab-review"
          className={`rounded-full px-4 py-2 text-xs font-bold ${tab === "review" ? "bg-ink text-white" : "border border-line bg-white text-slate-600"}`}>
          À vérifier
        </button>
        <button onClick={() => setTab("history")} data-testid="tab-history"
          className={`rounded-full px-4 py-2 text-xs font-bold ${tab === "history" ? "bg-ink text-white" : "border border-line bg-white text-slate-600"}`}>
          Historique vérifié
        </button>
      </div>

      {isLoading ? (
        <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>
      ) : !orders.length ? (
        <div className="rounded-2xl border border-dashed border-line bg-white p-10 text-center">
          <ReceiptText className="mx-auto h-9 w-9 text-slate-300" />
          <p className="mt-3 text-sm text-slate-500">{tab === "review" ? "Aucun paiement en attente de vérification." : "Aucun paiement vérifié pour l'instant."}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {orders.map((o) => {
            const claim = o.claim || {};
            return (
              <div key={o.id} data-testid={`vpayment-${o.id}`}
                className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-line bg-white p-4">
                <div className="min-w-0">
                  <p className="font-bold text-ink">{o.order_number} · <span className="text-brand">{formatPrice(o.total)}</span></p>
                  <p className="text-xs text-slate-500">{o.customer?.name} · {o.customer?.email} · <WhatsAppLink phone={o.customer?.phone} /></p>
                  <p className="mt-1 text-xs text-slate-500">
                    Moyen : <span className="font-bold text-ink">{claim.method_name || "—"}</span>
                    {claim.transaction_id && <> · Réf/TXID : <span className="font-mono font-bold text-ink">{claim.transaction_id}</span></>}
                  </p>
                  <p className="text-[11px] text-slate-400">Déclaré le {formatDateTime(claim.submitted_at)} · {o.items.map((i) => i.name).join(", ")}</p>
                  {claim.proof_path && (
                    <button onClick={() => viewProof(o)} data-testid={`vpayment-proof-${o.id}`}
                      className="mt-1 inline-flex items-center gap-1 text-xs font-bold text-brand">
                      <FileText className="h-3.5 w-3.5" /> Voir la preuve
                    </button>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {claim.status === "review" ? (
                    <>
                      <button onClick={() => verify(o, "confirm")} disabled={!!busy} data-testid={`vpayment-confirm-${o.id}`}
                        className="flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-2 text-xs font-bold text-white disabled:opacity-60">
                        {busy === o.id + "confirm" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />} Confirmer
                      </button>
                      <button onClick={() => verify(o, "reject")} disabled={!!busy} data-testid={`vpayment-reject-${o.id}`}
                        className="flex items-center gap-1.5 rounded-lg border border-rose-200 px-3 py-2 text-xs font-bold text-rose-600 disabled:opacity-60">
                        <X className="h-3.5 w-3.5" /> Refuser
                      </button>
                    </>
                  ) : (
                    <Pill tone={claim.status === "confirmed" || claim.status === "verified" ? "green" : "rose"}>
                      {claim.status === "confirmed" || claim.status === "verified" ? "✅ Confirmé" : "Refusé"}
                    </Pill>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </main>
  );
}
